import React from 'react';
import { css } from '@emotion/react';
import type { Option } from '@resi-media/resi-ui';
import { Box, Stack, Inline, Text, TabGroup, Tab, Draft } from '@resi-media/resi-ui';
import { JsonEditor } from 'json-edit-react';
import { useForm, Controller } from 'react-hook-form';
import { MOCK_TYPES } from '@preview/constants';
import { SafeDataObject } from '@preview/helpers/SafeDataObject';
import { useJsonEditorTheme } from '@preview/hooks/useJsonEditorTheme';
import type { FormValues, MockItem, MockTypes } from '../../types';
import { ChannelMockFields } from '../ChannelMockFields';

type MockTypeOption = { label: string; value: MockTypes };

type _Props = {
  mockTemplates: Record<string, MockItem>;
  onClose: () => void;
  onSubmit: (data: FormValues) => void;
};

const AddMockModal = ({ mockTemplates, onClose, onSubmit }: _Props) => {
  const jsonEditorTheme = useJsonEditorTheme();
  const [currentTab, setTabIndex] = React.useState(0);
  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      endpoint: '',
      description: '',
      data: '{}',
      mockType: 'other',
    },
  });

  // Create a new mock with the template for the selected type
  const createMockFromTemplate = (type?: MockTypes) => {
    if (!type || type === 'other') {
      return {
        endpoint: '',
        description: '',
        data: '{}',
      };
    }

    const template = mockTemplates[type];
    return {
      endpoint: template.endpoint,
      description: template.description,
      data: JSON.stringify(template.data, null, 2),
    };
  };

  return (
    <React.Fragment>
      <Draft.ModalBody>
        <Stack gap="m">
          <Box
            css={css`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Draft.FormField fieldLabel="Mock Type">
              <Controller
                control={control}
                name="mockType"
                render={({ field: { name, onBlur, onChange, ref, value } }) => {
                  const mockOptions: MockTypeOption[] = [
                    { value: 'channel', label: 'Channel' },
                    { value: 'feed', label: 'Feed' },
                    { value: 'playlist', label: 'Playlist' },
                    { value: 'video', label: 'Video' },
                    { value: 'other', label: 'Other' },
                  ];

                  return (
                    <Draft.Select<MockTypeOption>
                      ref={ref}
                      css={css`
                        width: 100%;
                      `}
                      dataTestId={name}
                      onBlur={onBlur}
                      onChange={(option: MockTypeOption) => {
                        onChange(option.value);
                        // Update form with template data for the selected type
                        const template = createMockFromTemplate(option.value);
                        reset({
                          ...template,
                          mockType: option.value,
                        });
                      }}
                      options={mockOptions}
                      value={mockOptions.find((v) => v.value === value)}
                    />
                  );
                }}
              />
            </Draft.FormField>

            <Draft.FormField fieldLabel="Endpoint">
              <Controller
                control={control}
                name="endpoint"
                render={({ field }) => (
                  <Draft.TextInput
                    {...field}
                    css={css`
                      width: 100%;
                    `}
                    placeholder="Enter endpoint URL"
                  />
                )}
              />
            </Draft.FormField>
          </Box>

          <Draft.FormField fieldLabel="Description">
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Draft.TextInput
                  {...field}
                  css={css`
                    width: 100%;
                  `}
                  placeholder="Enter description"
                />
              )}
            />
          </Draft.FormField>

          <Box
            css={css`
              border: 1px solid #f0f0f0;
              border-radius: 4px;
              overflow: hidden;
            `}
          >
            <TabGroup index={currentTab} isFullWidth onChange={(tabIndex) => setTabIndex(tabIndex)}>
              <Tab isFullWidth label="Fields" />
              <Tab isFullWidth label="JSON" />
            </TabGroup>
            {currentTab === 0 ? (
              <Box
                css={css`
                  padding: 16px;
                  max-height: 400px;
                  overflow-y: auto;
                `}
              >
                <Controller
                  control={control}
                  name="mockType"
                  render={({ field }) => {
                    const mockType = field.value;
                    const dataValue = watch('data');

                    if (mockType === MOCK_TYPES.CHANNEL) {
                      return (
                        <ChannelMockFields
                          dataValue={dataValue}
                          onChange={(data: string) => reset({ ...watch(), data })}
                        />
                      );
                    } else if (mockType === MOCK_TYPES.FEED) {
                      const dataWrapper = new SafeDataObject(dataValue);
                      return (
                        <Stack gap="s">
                          <Draft.FormField fieldLabel="Name">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updatedWrapper = dataWrapper.set('name', e.target.value);
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              value={dataWrapper.get('name', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Description">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updatedWrapper = dataWrapper.set('description', e.target.value);
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              value={dataWrapper.get('description', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Channel ID (embed ID for live player)">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setValue('data', dataWrapper.set('channelId', e.target.value).toString());
                              }}
                              placeholder="Leave empty to disable live player"
                              value={dataWrapper.get('channelId', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Branding Mode">
                            <Draft.Select<Option>
                              appendToBody
                              onChange={(option: Option) => {
                                const updatedWrapper = dataWrapper.set('branding', {
                                  ...dataWrapper.get('branding', {}),
                                  mode: option.value,
                                });
                                reset({
                                  ...watch(),
                                  data: updatedWrapper.toString(),
                                });
                              }}
                              options={[
                                { value: 'light', label: 'Light' },
                                { value: 'dark', label: 'Dark' },
                              ]}
                              value={{
                                value: dataWrapper.getNested('branding', 'mode', 'light'),
                                label:
                                  (dataWrapper.getNested('branding', 'mode', 'light') as string) === 'dark'
                                    ? 'Dark'
                                    : 'Light',
                              }}
                            />
                          </Draft.FormField>
                          <Text>Playlists data can be edited in JSON mode</Text>
                        </Stack>
                      );
                    } else if (mockType === MOCK_TYPES.PLAYLIST) {
                      const dataWrapper = new SafeDataObject(dataValue);
                      return (
                        <Stack gap="s">
                          <Draft.FormField fieldLabel="ID">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('id', e.target.value);
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.get('id', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Name">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('name', e.target.value);
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.get('name', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Description">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('description', e.target.value);
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.get('description', '')}
                            />
                          </Draft.FormField>
                          <Text>Videos data can be edited in JSON mode</Text>
                        </Stack>
                      );
                    } else if (mockType === MOCK_TYPES.VIDEO) {
                      const dataWrapper = new SafeDataObject(dataValue);
                      return (
                        <Stack gap="s">
                          <Draft.FormField fieldLabel="ID">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('id', e.target.value);
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.get('id', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Title">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('title', e.target.value);
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.get('title', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Description">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('description', e.target.value);
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.get('description', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Thumbnail URL">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('thumbnail', {
                                  ...dataWrapper.get('thumbnail', {}),
                                  url: e.target.value,
                                });
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.getNested('thumbnail', 'url', '')}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Duration (seconds)">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set('duration', Number(e.target.value));
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              type="number"
                              value={dataWrapper.get('duration', 0)}
                            />
                          </Draft.FormField>
                          <Draft.FormField fieldLabel="Tags (comma-separated)">
                            <Draft.TextInput
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newData = dataWrapper.set(
                                  'tags',
                                  e.target.value.split(',').map((tag: string) => tag.trim())
                                );
                                reset({
                                  ...watch(),
                                  data: newData.toString(),
                                });
                              }}
                              value={dataWrapper.get('tags', []).join(', ')}
                            />
                          </Draft.FormField>
                        </Stack>
                      );
                    } else {
                      return <Text>Please select a mock type or use JSON mode to edit data</Text>;
                    }
                  }}
                />
              </Box>
            ) : (
              <Box>
                <Controller
                  control={control}
                  name="data"
                  render={({ field }) => {
                    const dataWrapper = new SafeDataObject(field.value);

                    return (
                      <JsonEditor
                        data={dataWrapper.toJSON()}
                        indent={1}
                        keySort
                        maxWidth="100%"
                        rootName="data"
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setData={(data: any) => {
                          try {
                            field.onChange(JSON.stringify(data, null, 2));
                          } catch (error) {
                            console.error('Error updating JSON:', error);
                          }
                        }}
                        showCollectionCount="when-closed"
                        theme={jsonEditorTheme}
                      />
                    );
                  }}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </Draft.ModalBody>
      <Draft.ModalFooter>
        <Inline gap="md" justifyContent="flex-end">
          <Draft.Button label="Cancel" onClick={onClose}>
            Cancel
          </Draft.Button>
          <Draft.Button label="Add" onClick={handleSubmit(onSubmit)} variant="contained">
            Add
          </Draft.Button>
        </Inline>
      </Draft.ModalFooter>
    </React.Fragment>
  );
};

export default AddMockModal;
